import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin, Clock } from 'lucide-react';
import { PROFILE_DATA } from '../../data/config';
import WidgetContainer from '../WidgetContainer';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const CurrentLocation = ({ delay = 0 }) => {
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const time = new Date().toLocaleTimeString('en-US', {
        timeZone: PROFILE_DATA.location.timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      setCurrentTime(time);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <WidgetContainer size="1x2" delay={delay}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-cream-100">Current Location</h3>
        <MapPin 
          className="w-5 h-5 text-cream-200" 
          style={{
            animation: 'bounce-soft 3s ease-in-out infinite',
            filter: 'drop-shadow(0 0 8px rgba(255, 232, 200, 0.6))'
          }}
        />
      </div>

      <div className="space-y-4">
        {/* Map */}
        <div className="bg-coffee-700/20 rounded-lg overflow-hidden" style={{ height: '200px' }}>
          <MapContainer
            center={PROFILE_DATA.location.coords}
            zoom={12}
            style={{ height: '100%', width: '100%', filter: 'grayscale(100%)' }}
            zoomControl={false}
            scrollWheelZoom={false}
            dragging={false}
            touchZoom={false}
            doubleClickZoom={false}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
              attribution=""
            />
            <Marker position={PROFILE_DATA.location.coords}>
              <Popup>
                <div style={{ fontFamily: 'Fredoka, sans-serif', textAlign: 'center' }}>
                  <strong>{PROFILE_DATA.location.city}</strong>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>

        {/* Location */}
        <div className="bg-coffee-700/20 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-5 h-5 text-coffee-300" />
            <div>
              <div className="text-lg font-semibold text-cream-100">
                {PROFILE_DATA.location.city}
              </div>
              <div className="text-sm text-coffee-200">
                {PROFILE_DATA.location.country}
              </div>
            </div>
          </div>
        </div>

        {/* Time */}
        <div className="bg-coffee-700/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-coffee-300" />
            <div>
              <div className="text-2xl font-mono font-bold text-cream-100">
                {currentTime}
              </div>
              <div className="text-sm text-coffee-200">
                {PROFILE_DATA.location.timezone.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-gradient-to-r from-coffee-700/30 to-coffee-600/30 rounded-lg p-4 border border-coffee-600/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="live-glow w-2 h-2 bg-green-500 rounded-full"></span>
            <span className="text-sm font-medium text-cream-100">Available for work</span>
          </div>
          <div className="text-xs text-coffee-200">
            Open to remote opportunities and consulting projects
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
};

export default CurrentLocation;
